import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { normalizeRole } from "./roles";
import type { UserRole } from "./roles";

export type AuthUser = {
  uid: string;
  email: string;
  displayName: string | null;
  role: UserRole;
  isDisabled: boolean;
  clientId: string | null;
};

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  disabledMessage: string | null;
  signOutUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

class AccountAccessError extends Error {}

async function hydrateUser(firebaseUser: User): Promise<AuthUser | null> {
  const userRef = doc(db, "users", firebaseUser.uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    throw new AccountAccessError("Your account has not been provisioned. Contact an administrator.");
  }

  const data = snap.data();
  const email = firebaseUser.email ?? data?.email ?? "";
  const displayName = (data?.displayName ?? firebaseUser.displayName) ?? null;
  const role = normalizeRole(data?.role as UserRole | undefined, firebaseUser.uid, email);
  const isDisabled = Boolean(data?.isDisabled);
  const clientId = typeof data?.clientId === "string" ? data.clientId : null;

  if (isDisabled) {
    return null;
  }
  if (!role) {
    throw new AccountAccessError("Your account has an invalid role. Contact an administrator.");
  }

  await updateDoc(userRef, {
    email,
    displayName,
    updatedAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
  });

  return {
    uid: firebaseUser.uid,
    email,
    displayName,
    role,
    isDisabled,
    clientId,
  };
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disabledMessage, setDisabledMessage] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setLoading(true);
      setError(null);
      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }
      setDisabledMessage(null);

      hydrateUser(firebaseUser)
        .then((hydrated) => {
          if (!hydrated) {
            setDisabledMessage("Your account is disabled. Contact an administrator.");
            setUser(null);
            return signOut(auth);
          }
          setDisabledMessage(null);
          setUser(hydrated);
        })
        .catch((err) => {
          console.error("Failed to load auth user:", err);
          setUser(null);
          if (err instanceof AccountAccessError) {
            setDisabledMessage(err.message);
            return signOut(auth);
          }
          setError(err?.message || "Failed to load your account.");
        })
        .finally(() => {
          setLoading(false);
        });
    });

    return () => unsubscribe();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      disabledMessage,
      signOutUser: () => signOut(auth),
    }),
    [user, loading, error, disabledMessage]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

export function useAuthUser(): AuthUser | null {
  return useAuth().user;
}
